import React, { useMemo, useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Table,
  Typography,
  Switch,
  Space,
  Button,
  Spin,
  Alert,
  Collapse,
  Tag,
  Empty,
  message,
  Steps,
  Tooltip,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import RepairModel from "../lib/firebase/repairModel";
import { getTicketBids, acceptBid } from "../lib/api/vendorBidApi";
import {
  isLaunchDemoMode,
  getBidComparisonCleanScreenshot,
  getDebugMode,
} from "../helpers/constants";
import { useBreakpoints } from "../lib/breakpoints";
import {
  BID_COMPARISON_SCREENSHOT_TICKET_ID,
  getScreenshotMockBids,
  getScreenshotMockTicket,
} from "../helpers/bidComparisonScreenshotMock";
import vendorConversationAPI from "../lib/api/vendorConversationAPI";
import styled from "styled-components";
import {
  CheckCircleFilled,
  DollarOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import PageHeader from "../components/PageHeader";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../helpers/firebase";

const { Text, Paragraph } = Typography;

const VENDOR_LEGAL_NOTICE =
  "Verified partners pay to be listed on LandlordOps. Other bids may come from search. We have not checked them. Check license and insurance before you hire anyone. LandlordOps helps you compare quotes and message vendors. We are not responsible for their work.";

function timelineRank(timeline) {
  const t = String(timeline || "").toLowerCase();
  if (t.includes("24 hour")) return 1;
  if (t.includes("1-2 day")) return 2;
  if (t.includes("2-3 day")) return 3;
  if (t.includes("3-5 day")) return 4;
  if (t.includes("1 week") && !t.includes("1-2")) return 5;
  if (t.includes("1-2 week")) return 6;
  return 50;
}

function ticketSubtitleLine(ticket, ticketId, { debugPreview, cleanScreenshot }) {
  const issueTitle = ticket?.title?.trim() || "Maintenance ticket";
  const bits = [];
  if (ticket?.unitNumber) bits.push(`Unit ${ticket.unitNumber}`);
  else if (ticket?.unitId) bits.push(`Unit ${ticket.unitId}`);
  const cat = String(ticket?.category || "")
    .trim()
    .replace(/_/g, " ");
  if (cat) {
    bits.push(cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase());
  }
  if (debugPreview) {
    bits.push(cleanScreenshot ? "Sample" : "Preview");
  } else if (ticketId && String(ticketId).length >= 6) {
    bits.push(`#${String(ticketId).slice(0, 10)}`);
  }
  const tail = bits.length ? bits.join(" · ") : "";
  return tail ? `${issueTitle} · ${tail}` : issueTitle;
}

function buildRows(bids) {
  const active = (bids || []).filter(
    (b) => b.status === "pending" || b.status === "accepted"
  );
  const pool = active.length ? active : bids || [];
  const amounts = pool
    .map((b) => Number(b.final_bid_amount ?? b.bidAmount))
    .filter((n) => !Number.isNaN(n) && n > 0);
  const minAmt = amounts.length ? Math.min(...amounts) : null;
  const ranks = pool.map((b) => ({
    id: b.id,
    rank: timelineRank(b.timeline),
  }));
  const minRank =
    ranks.length > 0 ? Math.min(...ranks.map((r) => r.rank)) : null;

  return (bids || []).map((b) => {
    const amt = Number(b.final_bid_amount ?? b.bidAmount) || 0;
    const sentiments = [];
    if (minAmt != null && amt === minAmt && amt > 0) {
      sentiments.push("Best Value");
    }
    const r = timelineRank(b.timeline);
    if (minRank != null && r === minRank && pool.some((x) => x.id === b.id)) {
      sentiments.push("Fastest ETA");
    }
    if (
      b.submittedAt &&
      sentiments.length === 0 &&
      (b.negotiation_log || []).some((m) => m.role === "leo")
    ) {
      sentiments.push("Engaged");
    }
    if (sentiments.length === 0) {
      sentiments.push("-");
    }
    return {
      key: b.id,
      vendorName: b.vendorName || "Vendor",
      vendorIsPreferred: b.vendorIsPreferred === true,
      bidAmount: amt,
      eta: b.timeline || "-",
      sentiment: sentiments.join(" · "),
      raw: b,
    };
  });
}

function NegotiationChats({ bids }) {
  if (!bids?.length) {
    return <Empty description="No bid threads yet" />;
  }
  const items = bids.map((b) => {
    const log = b.negotiation_log || [];
    const unvetted = b.vendorIsPreferred !== true;
    return {
      key: b.id,
      label: `${b.vendorName || "Vendor"}${unvetted ? " (not a partner)" : ""} (${
        log.length
      } messages)`,
      children:
        log.length === 0 ? (
          <Text type="secondary">No messages in this thread.</Text>
        ) : (
          <ChatLog>
            {log.map((m, i) => (
              <ChatLine key={`${b.id}-${i}`}>
                <Tag
                  color={
                    m.role === "leo"
                      ? "blue"
                      : m.role === "vendor"
                      ? "green"
                      : "default"
                  }
                >
                  {m.role}
                </Tag>
                <Text>{m.content}</Text>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  {m.createdAt
                    ? new Date(m.createdAt).toLocaleString()
                    : ""}
                </Text>
              </ChatLine>
            ))}
          </ChatLog>
        ),
    };
  });
  return <Collapse items={items} />;
}

/**
 * Landlord-facing /repairs/:ticketId: Leo triage summary + optional full negotiation.
 */
const RepairTicketNegotiationView = ({ ticketId, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isMobile } = useBreakpoints();
  const debugBidPreview =
    getDebugMode() && ticketId === BID_COMPARISON_SCREENSHOT_TICKET_ID;
  const cleanScreenshotChrome =
    debugBidPreview && getBidComparisonCleanScreenshot();
  const [showFullNegotiation, setShowFullNegotiation] = useState(
    () => debugBidPreview && !getBidComparisonCleanScreenshot()
  );
  const bidsSeenIdsRef = useRef(new Set());
  const bidsListenerPrimedRef = useRef(false);
  const demoMode = isLaunchDemoMode();
  const [approvingBidId, setApprovingBidId] = useState(null);
  const [demoWorkOrder, setDemoWorkOrder] = useState(null);

  const demoWoStorageKey = ticketId ? `lo_demo_work_order_${ticketId}` : null;

  useEffect(() => {
    if (!demoMode || !demoWoStorageKey || typeof window === "undefined") return;
    try {
      const raw = sessionStorage.getItem(demoWoStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") setDemoWorkOrder(parsed);
      }
    } catch (_) {
      /* ignore */
    }
  }, [demoMode, demoWoStorageKey]);

  const persistDemoWorkOrder = (payload) => {
    setDemoWorkOrder(payload);
    if (demoWoStorageKey && typeof window !== "undefined") {
      try {
        sessionStorage.setItem(demoWoStorageKey, JSON.stringify(payload));
      } catch (_) {
        /* ignore */
      }
    }
  };

  const {
    data: ticket,
    isLoading: ticketLoading,
    error: ticketError,
  } = useQuery({
    queryKey: ["repairTicket", ticketId],
    queryFn: () => RepairModel.getById(ticketId),
    enabled: !!ticketId && !!user?.uid && !debugBidPreview,
  });

  const displayTicket = useMemo(() => {
    if (debugBidPreview && user?.uid) return getScreenshotMockTicket(user.uid);
    return ticket;
  }, [debugBidPreview, user?.uid, ticket]);

  const ownerOk =
    debugBidPreview ||
    (!!ticket &&
      (ticket.ownerId === user.uid || ticket.landlordId === user.uid));

  const {
    data: bidsPayload,
    isLoading: bidsLoading,
    error: bidsError,
  } = useQuery({
    queryKey: ["ticketBids", ticketId],
    queryFn: () => getTicketBids(ticketId),
    enabled: !!ticketId && !!user?.uid && ownerOk && !debugBidPreview,
  });

  const { data: convSearch } = useQuery({
    queryKey: ["repairConversationSearch", user?.uid, ticketId],
    queryFn: () =>
      vendorConversationAPI.searchConversations({
        userId: user.uid,
        userType: "landlord",
        conversationType: "maintenance_group",
        limit: 50,
      }),
    enabled: !!user?.uid && !!ticketId && !debugBidPreview,
  });

  const conversationMatch = useMemo(() => {
    const list = convSearch?.conversations || convSearch?.results || [];
    return list.find(
      (c) =>
        c?.context?.relatedTicketId === ticketId || c?.ticketId === ticketId
    );
  }, [convSearch, ticketId]);

  const bids = useMemo(
    () =>
      debugBidPreview
        ? getScreenshotMockBids()
        : bidsPayload?.bids || [],
    [debugBidPreview, bidsPayload]
  );

  const tableRows = useMemo(
    () =>
      buildRows(
        debugBidPreview
          ? getScreenshotMockBids()
          : bidsPayload?.bids || []
      ),
    [debugBidPreview, bidsPayload]
  );
  const bidsLoadingEffective = debugBidPreview ? false : bidsLoading;
  const bidsErrorEffective = debugBidPreview ? null : bidsError;

  const hasAcceptedBid = useMemo(
    () => {
      const list = debugBidPreview
        ? getScreenshotMockBids()
        : bidsPayload?.bids || [];
      return list.some((b) => b.status === "accepted");
    },
    [debugBidPreview, bidsPayload]
  );

  const handleApproveBid = async (row) => {
    if (debugBidPreview) return;
    const bidId = row.raw?.id;
    if (!bidId || !ticketId) return;
    setApprovingBidId(bidId);
    try {
      const res = await acceptBid(bidId, { ticketId });
      message.success("Bid approved. Work order is now active.");
      await queryClient.invalidateQueries({ queryKey: ["ticketBids", ticketId] });

      if (demoMode) {
        const amount =
          Number(row.bidAmount) > 0 ? Number(row.bidAmount) : 350;
        persistDemoWorkOrder({
          bidId,
          vendorName: row.vendorName || "Vendor",
          amount,
          workOrderId: res.workOrderId || null,
          approvedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      message.error(err?.message || "Could not approve bid");
    } finally {
      setApprovingBidId(null);
    }
  };

  const approveRef = useRef(handleApproveBid);
  approveRef.current = handleApproveBid;

  const tableColumnsStable = useMemo(
    () => [
      {
        title: "Vendor",
        key: "vendor",
        render: (_, row) => (
          <VendorNameCell>
            <span>{row.vendorName}</span>
            {row.vendorIsPreferred ? (
              <VerifiedPartnerBadge>
                <CheckCircleFilled aria-hidden />
                Verified partner
              </VerifiedPartnerBadge>
            ) : null}
          </VendorNameCell>
        ),
      },
      {
        title: "Bid amount",
        dataIndex: "bidAmount",
        key: "amt",
        render: (v) =>
          v > 0
            ? `$${v.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : "-",
      },
      { title: "ETA", dataIndex: "eta", key: "eta" },
      {
        title: "Quick read",
        dataIndex: "sentiment",
        key: "sentiment",
      },
      {
        title: "Action",
        key: "action",
        width: 196,
        align: "right",
        render: (_, row) => {
          const st = String(row.raw?.status || "").toLowerCase();
          const isPending = st === "pending";
          const disabled = !isPending || hasAcceptedBid || debugBidPreview;
          const loading = approvingBidId === row.raw?.id;
          const isPreferred = row.vendorIsPreferred === true;
          const label = st === "accepted" ? "Approved" : "Approve";

          const btn = isPreferred ? (
            <PreferredApproveButton
              type="primary"
              loading={loading}
              disabled={disabled}
              onClick={() => approveRef.current(row)}
            >
              {st === "accepted" ? "Approved" : "Approve & hire"}
            </PreferredApproveButton>
          ) : (
            <StandardApproveButton
              loading={loading}
              disabled={disabled}
              onClick={() => approveRef.current(row)}
            >
              {label}
            </StandardApproveButton>
          );

          return debugBidPreview ? (
            <Tooltip title="Sample only, not clickable">
              <span style={{ display: "inline-block" }}>{btn}</span>
            </Tooltip>
          ) : (
            btn
          );
        },
      },
    ],
    [hasAcceptedBid, approvingBidId, debugBidPreview]
  );

  const demoWorkOrderDisplay = useMemo(() => {
    if (debugBidPreview) return null;
    if (!demoMode) return null;
    if (demoWorkOrder) return demoWorkOrder;
    const list = debugBidPreview
      ? getScreenshotMockBids()
      : bidsPayload?.bids || [];
    const acc = list.find(
      (b) => String(b.status || "").toLowerCase() === "accepted"
    );
    if (!acc) return null;
    return {
      bidId: acc.id,
      vendorName: acc.vendorName || acc.contractorName || "Vendor",
      amount:
        Number(acc.bidAmount ?? acc.final_bid_amount ?? 0) > 0
          ? Number(acc.bidAmount ?? acc.final_bid_amount)
          : 350,
      workOrderId: acc.workOrderId ?? null,
      approvedAt: acc.updatedAt || acc.submittedAt,
    };
  }, [debugBidPreview, demoMode, demoWorkOrder, bidsPayload]);

  const ledgerUnitPhrase = useMemo(() => {
    if (displayTicket?.unitNumber) return `Unit ${displayTicket.unitNumber}`;
    if (displayTicket?.unitId) return `Unit ${displayTicket.unitId}`;
    return "this property";
  }, [displayTicket]);

  const compareBidsSubtitle = useMemo(
    () =>
      ticketSubtitleLine(displayTicket, ticketId, {
        debugPreview: debugBidPreview,
        cleanScreenshot: cleanScreenshotChrome,
      }),
    [displayTicket, ticketId, debugBidPreview, cleanScreenshotChrome]
  );

  useEffect(() => {
    bidsSeenIdsRef.current = new Set();
    bidsListenerPrimedRef.current = false;
  }, [ticketId]);

  useEffect(() => {
    if (debugBidPreview) return undefined;
    if (!ticketId || !user?.uid || !ownerOk) return undefined;
    const q = query(
      collection(db, "vendorBids"),
      where("ticketId", "==", ticketId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        queryClient.invalidateQueries({ queryKey: ["ticketBids", ticketId] });
        if (!bidsListenerPrimedRef.current) {
          snap.docs.forEach((d) => bidsSeenIdsRef.current.add(d.id));
          bidsListenerPrimedRef.current = true;
          return;
        }
        snap.docChanges().forEach((ch) => {
          if (ch.type !== "added") return;
          const id = ch.doc.id;
          if (bidsSeenIdsRef.current.has(id)) return;
          bidsSeenIdsRef.current.add(id);
          const d = ch.doc.data();
          const name = d.vendorName || d.contractorName || "Vendor";
          const amt = Number(d.bidAmount ?? d.final_bid_amount ?? 0);
          message.success(
            `New bid received: ${name} - $${amt.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
            5
          );
        });
      },
      (err) => {
        console.warn("[RepairTicketNegotiationView] vendorBids listener:", err);
      }
    );
    return () => unsub();
  }, [debugBidPreview, ticketId, user?.uid, ownerOk, queryClient]);

  if (!debugBidPreview && ticketLoading) {
    return (
      <Centered>
        <Spin size="large" />
      </Centered>
    );
  }

  if (!debugBidPreview && (ticketError || !ticket)) {
    return (
      <PageWrap>
        <Alert type="error" message="Could not load this repair ticket." />
      </PageWrap>
    );
  }

  if (!ownerOk) {
    return (
      <PageWrap>
        <Alert type="warning" message="You do not have access to this ticket." />
      </PageWrap>
    );
  }

  const PageShell = debugBidPreview ? ScreenshotPageWrap : PageWrap;

  return (
    <PageShell>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {debugBidPreview && !cleanScreenshotChrome && (
          <ScreenshotPreviewBanner role="status">
            <ScreenshotPreviewBannerInner>
              <ScreenshotPreviewTitle>Bid comparison preview</ScreenshotPreviewTitle>
              <ScreenshotPreviewSub>
                Sample data for screenshots. Not saved. Add{" "}
                <code>?clean=true</code> to hide this banner.
              </ScreenshotPreviewSub>
            </ScreenshotPreviewBannerInner>
          </ScreenshotPreviewBanner>
        )}

        <PageHeader
          title="Compare bids"
          subtitle={compareBidsSubtitle}
          icon={DollarOutlined}
        />

        {!debugBidPreview && conversationMatch?.id && (
          <Button
            type="default"
            icon={<MessageOutlined />}
            onClick={() =>
              navigate(`/conversations/${conversationMatch.id}${location.search}`)
            }
          >
            Open maintenance conversation
          </Button>
        )}

        <BidQuotesCard
          title={debugBidPreview ? "Vendor quotes" : "Leo AI summary & quotes"}
        >
          <LegalNoticeAlert
            type="warning"
            showIcon
            message="Before you hire"
            description={VENDOR_LEGAL_NOTICE}
          />
          {bidsErrorEffective && (
            <Alert
              type="error"
              showIcon
              message={
                (bidsErrorEffective && bidsErrorEffective.message) ||
                "Failed to load bids"
              }
              style={{ marginBottom: 16 }}
            />
          )}
          {bidsLoadingEffective ? (
            <Spin />
          ) : bids.length === 0 ? (
            <SourcingStatusCard>
              <LeoPulseDot aria-hidden />
              <SourcingTextWrap>
                <Text strong style={{ fontSize: 16, display: "block" }}>
                  Leo AI is sourcing vendors
                </Text>
                <Paragraph
                  type="secondary"
                  style={{ marginBottom: 0, marginTop: 8 }}
                >
                  Leo AI is currently sourcing and negotiating with local vendors
                  for this ticket. Bids will appear here as soon as vendors
                  respond.
                </Paragraph>
              </SourcingTextWrap>
            </SourcingStatusCard>
          ) : debugBidPreview && isMobile ? (
            <ScreenshotMobileBidList>
              {tableRows.map((row) => (
                <ScreenshotMobileBidCard
                  key={row.key}
                  $preferred={row.vendorIsPreferred === true}
                >
                  <ScreenshotMobileBidTop>
                    <ScreenshotMobileVendor>
                      {row.vendorName}
                      {row.vendorIsPreferred ? (
                        <VerifiedPartnerBadge>
                          <CheckCircleFilled aria-hidden />
                          Verified partner
                        </VerifiedPartnerBadge>
                      ) : null}
                    </ScreenshotMobileVendor>
                    <ScreenshotMobileAmount>
                      $
                      {row.bidAmount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </ScreenshotMobileAmount>
                  </ScreenshotMobileBidTop>
                  <ScreenshotMobileMeta>
                    <span>
                      <Text type="secondary">ETA</Text> {row.eta}
                    </span>
                    <span>
                      <Text type="secondary">Quick read</Text> {row.sentiment}
                    </span>
                  </ScreenshotMobileMeta>
                  <Tooltip title="Sample only, not clickable">
                    <span style={{ display: "block", marginTop: 12 }}>
                      {row.vendorIsPreferred ? (
                        <PreferredApproveButton type="primary" block disabled>
                          Approve & hire
                        </PreferredApproveButton>
                      ) : (
                        <StandardApproveButton block disabled>
                          Approve
                        </StandardApproveButton>
                      )}
                    </span>
                  </Tooltip>
                </ScreenshotMobileBidCard>
              ))}
            </ScreenshotMobileBidList>
          ) : (
            <ScreenshotTableWrap $active={debugBidPreview}>
              <Table
                size="middle"
                pagination={false}
                dataSource={tableRows}
                columns={tableColumnsStable}
                scroll={{ x: 860 }}
                rowClassName={(rec) =>
                  rec.vendorIsPreferred ? "bid-row-preferred" : ""
                }
              />
            </ScreenshotTableWrap>
          )}
          <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
            Quick read picks out the lowest bid and fastest ETA when we can. It
            is a hint, not a promise.
          </Paragraph>
        </BidQuotesCard>

        {demoWorkOrderDisplay && (
          <WorkOrderActiveCard
            title={
              <WorkOrderCardTitle>
                <span>Work order active</span>
                <Tag color="blue">Demo vision</Tag>
              </WorkOrderCardTitle>
            }
          >
            <Paragraph style={{ marginBottom: 20 }}>
              <Text strong>{demoWorkOrderDisplay.vendorName}</Text>
              <Text type="secondary">
                {" "}
                is hired for this ticket. Track the job from dispatch through
                closeout.
              </Text>
            </Paragraph>

            <DemoSectionLabel>Status tracker</DemoSectionLabel>
            <Steps
              size="small"
              current={1}
              items={[
                {
                  title: "Hired",
                  description: "Bid accepted",
                },
                {
                  title: "Scheduled",
                  description: "Vendor dispatch",
                },
                {
                  title: "In progress",
                  description: "On-site work",
                },
                {
                  title: "Completed",
                  description: "Job closed",
                },
              ]}
              style={{ marginBottom: 28 }}
            />

            <DemoSectionLabel>Completion photos</DemoSectionLabel>
            <PhotoPlaceholder>
              <Text type="secondary">
                Waiting for Vendor Completion Photos.
              </Text>
            </PhotoPlaceholder>

            <LedgerNote>
              This{" "}
              <strong>
                $
                {Number(demoWorkOrderDisplay.amount || 350).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}
              </strong>{" "}
              expense has been automatically added to the{" "}
              <strong>2026 Tax Ledger</strong> for{" "}
              <strong>{ledgerUnitPhrase}</strong>.
            </LedgerNote>
          </WorkOrderActiveCard>
        )}

        {!(debugBidPreview && cleanScreenshotChrome) && (
          <Card>
            <Space
              align="center"
              style={{ marginBottom: showFullNegotiation ? 16 : 0 }}
            >
              <Text strong>View full negotiation</Text>
              <Switch
                checked={showFullNegotiation}
                onChange={setShowFullNegotiation}
              />
            </Space>
            {showFullNegotiation && <NegotiationChats bids={bids} />}
          </Card>
        )}
      </Space>
    </PageShell>
  );
};

const LegalNoticeAlert = styled(Alert)`
  margin-bottom: 20px;
  border-width: 2px;
  font-size: 14px;
  line-height: 1.55;
`;

const VendorNameCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
`;

const VerifiedPartnerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 12px 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  font-family: var(--font-secondary);
  color: #5c4a1a;
  background: linear-gradient(
    125deg,
    #f5e6b8 0%,
    #e8d5a0 35%,
    rgba(44, 95, 45, 0.12) 100%
  );
  border: 1px solid rgba(184, 151, 74, 0.55);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.65) inset,
    0 2px 6px rgba(44, 95, 45, 0.12);

  .anticon {
    color: var(--color-primary);
    font-size: 12px;
  }
`;

const PreferredApproveButton = styled(Button)`
  && {
    height: 40px;
    padding: 0 20px;
    font-weight: 700;
    font-size: 15px;
    font-family: var(--font-secondary);
    border-radius: 10px;
    border: none;
    white-space: nowrap;
    background: linear-gradient(
      165deg,
      #3d8440 0%,
      var(--color-primary) 38%,
      var(--color-primary-dark) 100%
    );
    box-shadow:
      0 4px 16px rgba(44, 95, 45, 0.35),
      0 1px 0 rgba(255, 255, 255, 0.2) inset;
    color: #fff !important;
  }

  &&.ant-btn-primary:focus {
    color: #fff !important;
  }

  &&:hover:not(:disabled) {
    filter: brightness(1.06);
    color: #fff !important;
  }

  &&:disabled {
    opacity: 0.55;
    color: rgba(255, 255, 255, 0.92) !important;
  }

  &&.ant-btn-block {
    width: 100%;
    height: 44px;
    padding: 0 16px;
  }
`;

const StandardApproveButton = styled(Button).attrs({ type: "default" })`
  && {
    height: 34px;
    padding: 0 16px;
    font-weight: 600;
    font-size: 13px;
    font-family: var(--font-secondary);
    border-radius: 8px;
    white-space: nowrap;
    border: 1px solid var(--color-border-medium) !important;
    color: var(--color-primary-dark) !important;
    background: var(--color-bg-secondary) !important;
    box-shadow: none;
  }

  &&:hover:not(:disabled) {
    border-color: var(--color-primary-light) !important;
    color: var(--color-primary) !important;
  }

  &&.ant-btn-block {
    width: 100%;
    height: 40px;
  }
`;

const PageWrap = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px 48px;
`;

const ScreenshotPageWrap = styled(PageWrap)`
  max-width: 1040px;

  @media (max-width: 767px) {
    padding: 16px 12px 40px;
  }
`;

const ScreenshotPreviewBanner = styled.div`
  border-radius: 14px;
  padding: 14px 18px;
  background: linear-gradient(
    135deg,
    var(--color-primary-glow) 0%,
    rgba(44, 95, 45, 0.06) 100%
  );
  border: 1px solid rgba(44, 95, 45, 0.2);
  box-shadow: 0 4px 20px rgba(44, 95, 45, 0.08);
`;

const ScreenshotPreviewBannerInner = styled.div`
  max-width: 100%;
`;

const ScreenshotPreviewTitle = styled.div`
  font-family: var(--font-primary);
  font-weight: 700;
  font-size: 1rem;
  color: var(--color-primary-dark);
  letter-spacing: -0.02em;
  margin-bottom: 4px;
`;

const ScreenshotPreviewSub = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--color-text-light);

  code {
    font-size: 0.85em;
    padding: 2px 6px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(44, 95, 45, 0.15);
  }
`;

/** Quotes table card: same chrome for live tickets and debug screenshot. */
const BidQuotesCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: 0 4px 24px var(--shadow-light);
  overflow: hidden;

  .ant-card-head {
    border-bottom: 1px solid var(--color-border-light);
    background: linear-gradient(
      180deg,
      rgba(44, 95, 45, 0.07) 0%,
      transparent 100%
    );
    min-height: 52px;
  }

  .ant-card-head-title {
    font-family: var(--font-primary);
    font-weight: 700;
    font-size: 1.05rem;
    color: var(--color-text);
    letter-spacing: -0.02em;
  }

  .ant-card-body {
    padding: 20px;

    @media (max-width: 767px) {
      padding: 16px 14px;
    }
  }
`;

const ScreenshotTableWrap = styled.div`
  width: 100%;
  min-width: 0;

  .ant-table {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr > td:last-child {
    white-space: nowrap;
    vertical-align: middle;
  }

  .ant-table-tbody > tr > td {
    vertical-align: middle;
  }

  .ant-table-tbody > tr.bid-row-preferred > td {
    background: rgba(205, 175, 99, 0.07) !important;
  }

  .ant-table-tbody > tr.bid-row-preferred:hover > td {
    background: rgba(205, 175, 99, 0.11) !important;
  }

  ${(p) =>
    p.$active &&
    `
    .ant-table-thead > tr > th {
      background: var(--color-primary-glow) !important;
      color: var(--color-primary-dark) !important;
      font-weight: 600 !important;
      font-family: var(--font-secondary) !important;
      border-bottom: 1px solid rgba(44, 95, 45, 0.12) !important;
    }

    .ant-table-tbody > tr > td {
      font-family: var(--font-secondary);
      vertical-align: top;
    }

    .ant-table-tbody > tr:hover > td {
      background: rgba(44, 95, 45, 0.05) !important;
    }

    .ant-btn-primary:disabled {
      opacity: 0.55;
    }
  `}
`;

const ScreenshotMobileBidList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ScreenshotMobileBidCard = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$preferred",
})`
  border: 1px solid var(--color-border-light);
  border-radius: 14px;
  padding: 1rem 1.125rem;
  background: var(--color-bg-primary);
  box-shadow: 0 2px 14px rgba(44, 95, 45, 0.06);

  ${(p) =>
    p.$preferred &&
    `
    border-color: rgba(184, 151, 74, 0.5);
    box-shadow:
      0 2px 16px rgba(44, 95, 45, 0.1),
      0 0 0 1px rgba(205, 175, 99, 0.35);
  `}
`;

const ScreenshotMobileBidTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
`;

const ScreenshotMobileVendor = styled.div`
  font-family: var(--font-primary);
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text);
  line-height: 1.35;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const ScreenshotMobileAmount = styled.div`
  font-family: var(--font-primary);
  font-weight: 700;
  font-size: 1.2rem;
  color: var(--color-primary);
  flex-shrink: 0;
  text-align: right;
  letter-spacing: -0.02em;
`;

const ScreenshotMobileMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.875rem;
  font-family: var(--font-secondary);
  color: var(--color-text);
  line-height: 1.4;
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
`;

const ChatLog = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChatLine = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
`;

const SourcingStatusCard = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.25rem 1.25rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
`;

const LeoPulseDot = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(
    145deg,
    var(--color-primary-light),
    var(--color-primary-dark)
  );
  box-shadow: 0 0 0 0 rgba(44, 95, 45, 0.4);
  animation: leoPulseRing 1.8s ease-out infinite;
  @keyframes leoPulseRing {
    0% {
      box-shadow: 0 0 0 0 rgba(44, 95, 45, 0.38);
    }
    70% {
      box-shadow: 0 0 0 14px rgba(44, 95, 45, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(44, 95, 45, 0);
    }
  }
`;

const SourcingTextWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const WorkOrderActiveCard = styled(Card)`
  border-radius: 14px;
  border: 1px solid #c7d2fe;
  background: linear-gradient(
    165deg,
    #fafbff 0%,
    #f8fafc 45%,
    #f1f5f9 100%
  );
  box-shadow: 0 8px 30px rgba(30, 64, 175, 0.08);
  .ant-card-head {
    border-bottom-color: #e2e8f0;
  }
`;

const WorkOrderCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const DemoSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 10px;
`;

const PhotoPlaceholder = styled.div`
  min-height: 140px;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.65);
  margin-bottom: 1.25rem;
`;

const LedgerNote = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #475569;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(14, 165, 233, 0.08);
  border: 1px solid rgba(14, 165, 233, 0.2);
`;

export default RepairTicketNegotiationView;
